import { CallReceived, CallMade } from "@mui/icons-material";
import { DateTime } from "luxon";
import { UiCall } from "../../types";

type CallIconConfig = {
  icon: React.ElementType;
  color: string;
};

const getDirectionIcon = (isIncoming: boolean) => (isIncoming ? CallReceived : CallMade);

const getStatusColor = (call: UiCall): string => {
  const status = call.status?.toLowerCase();
  const isIncoming = call.type === "incoming";

  switch (status) {
    case "completed":
      return isIncoming ? "success.main" : "primary.main";
    case "no-answer":
      return isIncoming ? "error.main" : "warning.main";
    case "busy":
      return "warning.main";
    case "failed":
    case "canceled":
    case "cancelled":
      return "text.secondary";
    case "ringing":
    case "in-progress":
      return "info.main";
    default:
      return "text.secondary";
  }
};

export const getCallIconConfig = (call: UiCall): CallIconConfig => {
  const isIncoming = call.type === "incoming";

  return {
    icon: getDirectionIcon(isIncoming),
    color: getStatusColor(call),
  };
};

export const getCallDirectionLabel = (call: UiCall): string => {
  return call.type === "incoming" ? "Incoming" : "Outgoing";
};

export const getCallStatusLabel = (call: UiCall): string => {
  const status = call.status?.toLowerCase();
  const isIncoming = call.type === "incoming";

  if (status === "no-answer") return isIncoming ? "Missed" : "No answer";
  if (status === "busy") return "Busy";
  if (status === "failed") return "Failed";
  if (status === "canceled" || status === "cancelled") return "Canceled";
  if (status === "ringing") return "Ringing";
  if (status === "in-progress") return "In call";

  return isIncoming ? "Incoming" : "Outgoing";
};

export const isUnansweredCall = (call: UiCall): boolean => {
  const status = call.status?.toLowerCase();
  return status === "no-answer" && call.type === "incoming";
};

const UNCONNECTED_STATUSES = ["no-answer", "busy", "failed", "canceled", "cancelled"];

export const formatCallDateTime = (date: Date): string => {
  return DateTime.fromJSDate(date).setLocale("en").toFormat("EEE, LLL d, yyyy, h:mm a");
};

export const formatCallDuration = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];

  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (minutes > 0) parts.push(`${minutes} min`);
  if (seconds > 0) parts.push(`${seconds} sec`);

  return parts.join(" ");
};

export const formatCallHistoryLabel = (call: UiCall): string => {
  const dateLabel = formatCallDateTime(call.timestamp);

  const status = call.status?.toLowerCase();
  const seconds = call.durationSeconds ?? 0;

  if (!seconds || seconds <= 0 || UNCONNECTED_STATUSES.includes(status)) return dateLabel;

  const durationLabel = formatCallDuration(seconds);

  return durationLabel ? `${dateLabel} ∙ ${durationLabel}` : dateLabel;
};
